import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Tabs from '../components/tabs/Tabs';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import { getClientDetail } from '../services/ClientsService';
import GeneralDataTab from '../organisms/ClientDetail/GeneralDataTab';
import SolicitudesTab from '../organisms/ClientDetail/SolicitudesTab';
import DocumentationTab from '../organisms/ClientDetail/DocumentationTab';
import { routes } from '../routes/routePaths';
import { useClientTheme } from '../context/ThemeContext';

interface ClientDetailData {
  id: number;
  cuit: string;
  verified: boolean;
  basic_data: {
    company_name: string;
    company_address: string;
    state: string;
    city: string;
    phone_number: string;
    email: string;
    legal_entity: string;
    activity: string;
    iva_status: string;
    date_incorporation: string;
    economic_group: string | null;
  };
  corporate_structure: Array<{
    ci: string;
    first_name: string;
    last_name: string;
    ownership_interest: string;
    position: string;
    assets: string;
    value: number;
  }>;
  files: any[];
  application_files?: any[];
  products: any[];
  manual_total_amount: boolean;
  requested_amount: any[];
  reason: string | null;
  review: string | null;
  extra_comments: string | null;
  has_valid_corporate_composition: boolean;
  signers: any[];
  referrer_name: string;
  officer: string;
  total_ars: number | null;
  total_usd: number | null;
}

const ClientDetail = () => {
  const { cuit } = useParams<{ cuit: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { clientTheme } = useClientTheme();
  const [clientData, setClientData] = useState<ClientDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cuit) {
      fetchClientDetail();
    }
  }, [cuit]);

  const fetchClientDetail = async () => {
    if (!cuit) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getClientDetail(cuit);
      if (response.success && 'data' in response) {
        setClientData(response.data);
      } else {
        setError('Error al cargar los datos del cliente');
        navigate(routes.clientsList);
      }
    } catch (err) {
      setError('Error al cargar los datos del cliente');
      navigate(routes.clientsList);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(routes.clientsList);
  };

  const PlaceholderTab = ({ title }: { title: string }) => (
    <Card spacing="4" display="flex" justifyContent="center" alignItems="center" height="200px">
      <P type="1" weight="regular" modifier="weak">
        {title} - Próximamente
      </P>
    </Card>
  );

  if (loading) {
    return (
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={8} columns={4} />
        </Card>
      </Card>
    );
  }

  if (error || !clientData) {
    return (
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <Card display='flex' justifyContent='center' alignItems='center' height='300px'>
          <P type="1" weight="regular" modifier="weak">{error || 'Cliente no encontrado'}</P>
        </Card>
      </Card>
    );
  }

  const tabs = [
    {
      label: 'Datos generales',
      content: <GeneralDataTab clientData={clientData} />
    },
    {
      label: 'Documentación',
      content: <DocumentationTab files={clientData.files} application_files={clientData.application_files} cuit={clientData.cuit} clientTheme={!!clientTheme} />
    },
    {
      label: 'Solicitudes',
      content: <SolicitudesTab cuit={clientData.cuit} />
    }
  ];

  return (
    <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
      {/* Header with back button and title */}
      <Card display='flex' alignItems='flex-start' justifyContent='space-between'> 
        <Card display='flex' alignItems='center' gap='16px' margin={{ bottom: '4' }}>
          <Button 
            icon='arrow_left_outline'
            variant='tertiary'
            size='sm'
            iconAlign='center'
            onClick={handleGoBack}
            aria-label="Volver a clientes"
          />
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Detalle {clientData.basic_data.company_name}
          </H>
        </Card>
        <Button variant='secondary' onClick={() => navigate(routes.clients)}>
          Alta de nueva carpeta
        </Button>
      </Card>
      {/* Tabs */}
      <Card margin={{ top: '4' }}>
        <Tabs tabs={tabs} defaultActiveTab={0} />
      </Card>
    </Card>
  );
};

export default ClientDetail;